import { forwardRef, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '../lib/cn';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  className?: string;
}

/**
 * A native select.
 *
 * Native rather than a custom listbox on purpose: on mobile it becomes the
 * platform picker, it works with no JavaScript, and its keyboard behaviour is
 * correct in every locale without being reimplemented. A custom Radix select is
 * warranted only where an option needs rich content — the attribution picker,
 * for instance, uses radio cards instead, because that choice is a legal
 * statement and deserves explanation per option.
 *
 * The chevron is positioned at the inline end, so it moves to the left in
 * Arabic without a second rule.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, children, ...props },
  ref,
) {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          'h-11 w-full appearance-none rounded-md border border-border-strong bg-surface',
          'ps-3 pe-10 text-base text-text',
          'transition-colors duration-[--hs-duration-fast]',
          'hover:border-text-muted',
          'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-60',
          'aria-[invalid=true]:border-danger',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 text-text-muted"
        aria-hidden="true"
      />
    </div>
  );
});
